"use client";

import type React from "react";

import Button from "@/components/ReusableButton";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ECHOPAY_SVG } from "@/assets/svgs";
import { useRouter, useSearchParams } from "next/navigation";
import InputField from "@/components/InputField";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/components/ToastProvider";
import { useRegister } from "@/hooks/useRegister";
import PhoneInputField from "@/components/PhoneInputField";
import { isValidPhoneNumber } from "libphonenumber-js/max";

type Step = {
  number: number;
  name: string | null;
  logicalStep: number;
};

export default function RegisterUI() {
  const router = useRouter();
  const { register, isRegistering } = useRegister();
  const searchParams = useSearchParams();
  const { showSuccess, showError } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    phone: "", // will store E.164 e.g. +2348012345678
    password: "",
    confirmPassword: "",
    referral_code: "",
  });
  // Pre-fill referral code from URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setFormData((prev) => ({ ...prev, referral_code: ref }));
    }
  }, [searchParams]);


  const emailValidation = useMemo(() => {
    const robustEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      formData.email.trim() === "" || robustEmailRegex.test(formData.email)
    );
  }, [formData.email]);

  const phoneValidation = useMemo(() => {
    if (formData.phone === "") return true;
    try {
      return isValidPhoneNumber(formData.phone);
    } catch {
      return false;
    }
  }, [formData.phone]);

  const referralCodeValidation = useMemo(() => {
    // Optional field — only validate format if something is typed
    if (formData.referral_code.trim() === "") return true;
    const referralRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    return referralRegex.test(formData.referral_code.trim());
  }, [formData.referral_code]);

  const passwordRequirements = useMemo(
    () => [
      { label: "Minimum of 8 characters", met: formData.password.length >= 8 },
      { label: "Lowercase letter(s)", met: /[a-z]/.test(formData.password) },
      { label: "Uppercase letter(s)", met: /[A-Z]/.test(formData.password) },
      {
        label: "A special character (!@#$%*)",
        met: /[!@#$%*]/.test(formData.password),
      },
      { label: "A number (123...)", met: /\d/.test(formData.password) },
    ],
    [formData.password],
  );

  const steps: Step[] = [
    { number: 1, name: "Personal Info", logicalStep: 1 },
    { number: 2, name: null, logicalStep: 1 },
  ];

  const LOGICAL_STEPS = 1;

  // Which UI step belongs to which logical step
  const logicalStepMap: Record<number, number> = {
    1: 1,
    2: 1,
  };

  const isStep1Valid = useMemo(
    () =>
      formData.email && formData.phone && emailValidation && phoneValidation,
    [formData.email, formData.phone, emailValidation, phoneValidation],
  );

  const areAllPasswordRequirementsMet = useMemo(
    () => passwordRequirements.every((req) => req.met),
    [passwordRequirements],
  );

  const isStep2Valid = useMemo(
    () =>
      formData.password &&
      formData.confirmPassword &&
      formData.password === formData.confirmPassword &&
      areAllPasswordRequirementsMet,
    [
      formData.password,
      formData.confirmPassword,
      areAllPasswordRequirementsMet,
    ],
  );

  const passwordsMatch = useMemo(
    () =>
      formData.confirmPassword === "" ||
      formData.password === formData.confirmPassword,
    [formData.password, formData.confirmPassword],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleContinue = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (currentStep === 1) {
        if (isStep1Valid) {
          setCompletedSteps((prev) => [...prev, 1]);
          setCurrentStep(2);
        }
      } else if (currentStep === 2) {
        if (isStep2Valid) {
          setCompletedSteps((prev) => [...prev, 2]);

          // Call the actual API
          register(
            {
              email: formData.email,
              phone: formData.phone,
              password: formData.password,
              confirm_password: formData.confirmPassword,
              referral_code: formData.referral_code.trim() || undefined, // omit if empty
            },
            {
              onSuccess: (response) => {
                showSuccess("Registration successful", response.data.message);
                router.push("/verify-email");
              },
              onError: (error: any) => {
                showError("Registration failed", error.message);
              },
            },
          );
        }
      }
    },
    [
      currentStep,
      isStep1Valid,
      isStep2Valid,
      formData,
      router,
      register,
      showSuccess,
    ],
  );

  const progressWidth = useMemo(() => {
    const completedLogicalSteps = new Set(
      completedSteps.map((step) => logicalStepMap[step]),
    ).size;

    const currentLogicalStep = logicalStepMap[currentStep];

    const isCurrentLogicalCompleted =
      completedLogicalSteps >= currentLogicalStep;

    const halfProgress = isCurrentLogicalCompleted ? 0 : 0.5;

    const progress = (completedLogicalSteps + halfProgress) / LOGICAL_STEPS;

    return `${progress * 100}%`;
  }, [completedSteps, currentStep]);

  return (
    <div className="flex min-h-screen bg-[#f8f8f8]">
      {/* Left Side - Branding */}
      <div className="border-[6px] sm:border-[8px] border-[#009dff43] hidden z-50 lg:w-1/2 rounded-[20px] sm:rounded-[24px] relative lg:block m-3 sm:m-4 h-auto">
        <div className="bg-[url('/auth_bg.svg')] rounded-[14px] sm:rounded-[17px] w-full bg-cover h-full">
          <div className="p-3 sm:p-4 pb-32 sm:pb-36 md:pb-40 flex flex-col justify-between h-full">
            {/* Logo */}
            <Link href="/">
              <div>
                {ECHOPAY_SVG().resolvaOne({
                  className:
                    "w-[120px] h-[28px] sm:w-[130px] sm:h-[30px] md:w-[144.25px] md:h-[33px]",
                })}
              </div>
            </Link>

            <div className="bg-[#009dff1b] border-[6px] sm:border-[8px] border-[#009DFF54] rounded-[16px] sm:rounded-[20px] p-3 sm:p-4">
              <h3 className="text-[#373737] font-medium text-[20px] sm:text-[22px] md:text-[25px] leading-[30px] sm:leading-[34px] md:leading-[37px] tracking-[-0.9px] font-inter mb-2 sm:mb-2.5 md:mb-3">
                Send Crypto. <br /> Receive Cash. Instantly.
              </h3>
              <p className="font-inter font-normal text-[14px] sm:text-[15px] md:text-[16px] tracking-[-0.5px] text-[#373737] leading-relaxed">
                Family gets bank alerts. Shop customers scan and pay. You and
                your business get instant fiat settlements with Resolva.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 py-[4rem] sm:py-[5rem] px-4 sm:px-8 md:px-12 lg:p-12 flex items-center justify-center relative h-auto">
        <div className="block lg:hidden absolute top-0 left-0 right-0 h-3 sm:h-4 bg-[#0046A7]"></div>
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link href="/">
            <div className="lg:hidden mb-[3rem] sm:mb-[4rem] flex justify-center">
              {ECHOPAY_SVG().resolvaOne({
                className: "w-[130px] h-[30px] sm:w-[144px] sm:h-[33px]",
              })}
            </div>
          </Link>

          {/* Back Button */}
          {currentStep > 1 && (
            <Button
              type="button"
              onClick={() => {
                const newStep = currentStep - 1;
                const currentLogicalStep = logicalStepMap[currentStep];

                setCompletedSteps((prev) =>
                  prev.filter(
                    (step) => logicalStepMap[step] !== currentLogicalStep,
                  ),
                );

                setCurrentStep(newStep);
              }}
              className="!text-[#010721] border-none bg-transparent hover:bg-transparent !px-0 flex items-center font-normal text-xs sm:text-sm md:text-base tracking-[0.5px] align-middle mb-4 sm:mb-5 md:mb-6"
            >
              <ArrowLeft className="w-4 h-4 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5 mr-1.5 sm:mr-2" />
              Go Back
            </Button>
          )}

          {/* Form Header */}
          <div className="mb-8 sm:mb-10 md:mb-12">
            <h2 className="text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px] xl:text-[32px] 2xl:text-[36px] leading-[28px] sm:leading-[32px] md:leading-[38px] lg:leading-[44px] tracking-[-2px] font-semibold text-[#010721] mb-1.5 sm:mb-2 font-inter">
              Create Your Account
            </h2>
            <p className="text-[#010721] text-[12px] sm:text-[13px] md:text-[14px] font-roboto leading-[18px] sm:leading-[19px] md:leading-[20px] tracking-[0.1px] font-normal">
              {"Let's get to know you right away!"}
            </p>
          </div>

          <div className="mb-6 sm:mb-7 md:mb-8">
            {/* Step labels */}
            <div className="flex items-center justify-center mb-2 sm:mb-2.5 md:mb-3">
              {steps.map((step) => {
                const isActive =
                  logicalStepMap[currentStep] === step.logicalStep ||
                  completedSteps.some(
                    (s) => logicalStepMap[s] === step.logicalStep,
                  );

                return (
                  <span
                    key={step.number}
                    className={`font-normal even:hidden text-[9px] sm:text-[10px] md:text-[11px] lg:text-[12px] leading-[100%] text-center ${
                      isActive ? "text-[#010721]" : "text-[#8c8c8c]"
                    }`}
                  >
                    {step.name ?? (
                      <span className="invisible">placeholder</span>
                    )}

                    <div
                      className={`w-[2.5px] h-[2.5px] sm:w-[3px] sm:h-[3px] rounded-full mx-auto mt-0.5 sm:mt-1 ${
                        isActive ? "bg-[#010721]" : "bg-[#CDDBEF]"
                      }`}
                    />
                  </span>
                );
              })}
            </div>

            {/* Progress bar */}
            <div className="relative h-1.5 sm:h-2 bg-[#CDDBEF] rounded-full">
              <div
                className="absolute left-0 top-0 h-full bg-[#0046A7] rounded-full transition-all duration-300"
                style={{ width: progressWidth }}
              />
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleContinue}
            className="space-y-6 sm:space-y-7 md:space-y-8"
          >
            {/* Step 1: Contact Information */}
            {currentStep === 1 && (
              <>
                <InputField
                  id="email"
                  name="email"
                  type="email"
                  label="Email Address"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!emailValidation}
                  errorMessage="Invalid email address"
                />
                <PhoneInputField
                  value={formData.phone}
                  onChange={(val) => {
                    setFormData((prev) => ({ ...prev, phone: val }));
                    setPhoneTouched(true);
                  }}
                  error={phoneTouched && !phoneValidation}
                  errorMessage="Invalid phone number"
                  bgColor="#f8f8f8"
                />
                <InputField
                  id="referralCode"
                  name="referral_code"
                  type="text"
                  label="Referral Code (Optional)"
                  placeholder="Enter Referral Code"
                  value={formData.referral_code}
                  onChange={handleInputChange}
                  error={!referralCodeValidation}
                  errorMessage="Invalid referral code"
                />
              </>
            )}

            {/* Step 2: Password */}
            {currentStep === 2 && (
              <>
                <InputField
                  id="password"
                  name="password"
                  label="Password"
                  placeholder="****************"
                  value={formData.password}
                  onChange={handleInputChange}
                  showPasswordToggle={true}
                />

                {/* Password Requirements */}
                <div className="p-2.5 sm:p-3 md:p-[12px] rounded-[8px] border border-[#E2E2E2] space-y-2 sm:space-y-2.5 md:space-y-3">
                  <p className="text-xs sm:text-[13px] md:text-sm font-medium text-[#010721]">
                    Your password should contain:
                  </p>
                  <div className="space-y-1.5 sm:space-y-2">
                    {passwordRequirements.map((req, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 sm:gap-2.5 md:gap-3"
                      >
                        {req.met ? (
                          <div className="w-4 h-4 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5 flex items-center justify-center flex-shrink-0">
                            {ECHOPAY_SVG().fillCheck()}
                          </div>
                        ) : (
                          <div className="w-4 h-4 sm:w-[18px] sm:h-[18px] md:w-5 md:h-5 flex items-center justify-center flex-shrink-0">
                            {ECHOPAY_SVG().circleOutline()}
                          </div>
                        )}
                        <span
                          className={`text-[11px] sm:text-[12px] font-normal ${
                            req.met ? "text-[#010721]" : "text-[#828783]"
                          }`}
                        >
                          {req.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Confirm Password Input */}
                <InputField
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm Password"
                  placeholder="****************"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  showPasswordToggle={true}
                  error={!passwordsMatch}
                  errorMessage="Passwords do not match"
                  errorIcon={ECHOPAY_SVG().errorIcon()}
                />
              </>
            )}

            <Button
              type="submit"
              disabled={
                (currentStep === 1 && !isStep1Valid) ||
                (currentStep === 2 && !isStep2Valid)
              }
              fullWidth
              className="mt-6 sm:mt-7 md:mt-8 h-11 sm:h-12 md:h-13 lg:h-14 text-xs sm:text-[13px] md:text-sm lg:text-base"
              isLoading={isRegistering}
            >
              Continue
            </Button>
          </form>

          {/* Footer Links */}
          {currentStep === 1 && (
            <div className="mt-6 sm:mt-7 md:mt-8 text-center space-y-4 sm:space-y-5 md:space-y-6">
              <p className="text-[#8c8c8c] text-xs sm:text-[13px] md:text-sm">
                Already have an account?{" "}
                <Link
                  href="/login"
                  className="text-[#010721] font-medium underline"
                >
                  Sign in.
                </Link>
              </p>
              <p className="text-[11px] sm:text-xs md:text-sm text-[#8c8c8c]">
                By signing up, you agree to our{" "}
                <Link href="/terms" className="text-[#010721] underline">
                  terms of service
                </Link>{" "}
                and{" "}
                <Link href="/privacy" className="text-[#010721] underline">
                  data processing policy
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
